import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditModule from "./AddEditModuleNaming";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import ModuleActionButton from "./ModuleNamingActionButton";
import Confirmation from "../../../components/Confirmation";

const ModuleNamings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [modulesList, setModulesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [moduleId, setModuleId] = useState(null);
  const [showAddEditModule, setShowAddEditModule] = useState(false);
  const [showModuleDelete, setShowModuleDelete] = useState(false);

  const toggleShowAddEditModule = () => {
    if (showAddEditModule) {
      setEdit(false);
      setModuleId(null);
    }
    setShowAddEditModule(!showAddEditModule);
  };

  const toggleShowDelete = () => {
    if (showModuleDelete) {
      setModuleId(null);
    }
    setShowModuleDelete(!showModuleDelete);
  };

  const fetchModule = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_MODULE_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const modulesList = data.data.data;
        setModulesList(modulesList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setModulesList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const modulesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: modulesList[i]?.id,
        organization_type: modulesList[i]?.organization_type?.name
          ? modulesList[i]?.organization_type?.name
          : "",
        module: modulesList[i]?.module?.name
          ? modulesList[i]?.module?.name === "Contacts"
            ? "Customer"
            : modulesList[i]?.module?.name?.slice(0, -1)
          : "",
        name: modulesList[i]?.name ? modulesList[i]?.name : "",
        action: (
          <>
            <ModuleActionButton
              id={modulesList[i]?.id}
              setEdit={setEdit}
              setModuleId={setModuleId}
              toggleShowAddEditModule={toggleShowAddEditModule}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const modules = modulesGenerator(modulesList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "module",
      text: "Module",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchModule(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchModule(limit, page);
  };

  // Single Module Delete
  const handleModuleDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_MODULE_LIST}/${moduleId}`);
      setLoading(false);
      fetchModule();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add Module */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Module Naming" : "Add Module Naming"}
              handleOpen={() => {}}
              handleClose={() => {}}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Module Naming</span>
                </div>
              }
              className={"sm:max-w-3/4"}
              open={showAddEditModule}
              buttonClassName={""}
              setOpen={setShowAddEditModule}
            >
              <AddEditModule
                edit={edit}
                toggleShowAddEditModule={toggleShowAddEditModule}
                id={moduleId}
                fetchModuleData={() => fetchModule(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={modules}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Module Naming"}
        open={showModuleDelete}
        setOpen={setShowModuleDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete module?"}
        okAction={async () => {
          handleModuleDelete();
          toggleShowDelete();
          fetchModule();
        }}
        cancelAction={() => {
          setModuleId(null);
          setShowModuleDelete(!showModuleDelete);
        }}
      />
    </>
  );
};

export default ModuleNamings;
